import { defineComponent as _defineComponent } from 'vue'
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { CompanyResponse } from '@/@types/responses/CompanyResponses';
import WorkspaceViewPage from '@/components/pages/workspace/WorkspaceViewPage/WorkspaceViewPage.vue';
import { getOpenableArticleIds, isSkippableArticleError } from '@/helpers/workspaceArticleSelection';
import { Article } from '@/models/article/Article';
import { ArticleComment } from '@/models/article/ArticleComment';
import { Company } from '@/models/Company';
import { Workspace } from '@/models/Workspace';
import { Logger } from '@/other/Logger';
import * as routerNames from '@/routerNames';
import { BROWSE_SHARED, HOME, INACTIVE_COMPANY, LOGIN } from '@/routerNames';
import apiService from '@/services/ApiService';
import articleModeService from '@/services/article/ArticleModeService';
import authService from '@/services/AuthService';
import navigationService from '@/services/NavigationService';
import notificationService from '@/services/NotificationService';
import urlService from '@/services/UrlService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import store from '@/store';
import { commitSetArticleComments } from '@/store/commits/commentsSidebarCommits';
import {
  commitSetArticleIdToExpand,
  commitSetArticleMode,
  commitSetAutoPlay,
  commitSetSharedArticleCompanyLogo,
} from '@/store/commits/sharedCommits';
import { commitClearDiffState } from '@/store/commits/diffCommits';
import { dispatchFetchDiff } from '@/store/dispatchers/diffDispatchers';
import {
  commitSetSelectedArticle,
  commitSetWorkspaceViewCommentsCount,
  commitWorkspaceViewReset,
  commitWorkspaceViewSetActiveTextTabId,
} from '@/store/commits/workspaceViewCommits';
import { Mutations } from '@/store/mutations';
import { AxiosError } from 'axios';
import { Loading } from 'element-ui';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'BrowsePage',
  props: {
  workspaceId: { type: String, default: null },
  articleId: { type: String, default: null },
  textTabSlug: { type: String, default: null },
  paragraphSlug: { type: String, default: null },
  sharedToken: { type: String, default: null },
  accessType: { type: String, default: null },
  mode: { type: String, default: null },
},
  setup(__props) {

const props = __props

const log = new Logger('BrowsePage');



const router = useRouter();

const isLoaded = ref(false);
const workspace = ref<Workspace | null>(null);
const sharedArticle = ref<Article | null>(null);
const loader = ref<any>(null);
const viewMode = ref<ArticleEditMode>(ArticleEditMode.VIDEO);

const resetViewState = () => {
  commitWorkspaceViewReset();
  commitSetArticleMode(undefined);
  workspace.value = null;
  sharedArticle.value = null;
};

const setViewMode = () => {
  viewMode.value = props.mode === ArticleEditMode.NOTES ? ArticleEditMode.NOTES : ArticleEditMode.VIDEO;
};

const handleAuthRedirect = () => {
  if (authService.isAuthorized()) {
    router.push({ name: HOME });
  } else {
    router.push({ name: LOGIN, query: { redirect: router.currentRoute.fullPath } });
  }
};

const handleSharedContentError = (e: unknown) => {
  store.commit('setCompanyLoading', false);
  const error = e as AxiosError<{ error: string; company?: CompanyResponse }>;

  if (error.response?.data?.company) {
    store.commit(Mutations.SET_COMPANY_STATUS, error.response.data.company.isActivated);
    navigationService.navigate({ name: INACTIVE_COMPANY });
    return;
  }
  if (error.response && [404, 422].includes(error.response.status)) {
    apiService.handleResponseError(error);
    handleAuthRedirect();
    return;
  }
  apiService.handleResponseError(error);
};

const loadSharedArticleCompanyLogo = async (articleId: string) => {
  try {
    const res = await apiService.browse.getSharedArticleCompanyLogo(articleId);
    return res.data.logo;
  } catch (err) {
    return null;
  }
};

const loadComments = async (articleId: string) => {
  try {
    const response = await apiService.comment.getAll(articleId);
    const comments = response.data.comments.map(({ comment, profileImage }) =>
      ArticleComment.parse({ ...comment, profileImage })
    );
    commitSetArticleComments(comments);
    commitSetWorkspaceViewCommentsCount(comments.length);
  } catch (error) {
    apiService.handleResponseError(error);
  }
};

const updateStoreForSharedArticle = () => {
  commitSetSelectedArticle(sharedArticle.value);
  workspaceService.setState('articles', [sharedArticle.value]);
  workspaceService.setState('isSharedMode', true);
  // SNAP-2311: so ImageView / FileAttachment / resignStoredHtml can present it to the
  // now-scoped `/browse/shared/image|file-attachment/url` endpoints.
  workspaceService.setState('sharedToken', props.sharedToken);
};

const initSharedContent = async () => {
  try {
    const isPrivate = props.accessType === 'private';
    if (isPrivate && authService.isGuest()) {
      handleAuthRedirect();
      return;
    }

    const response = isPrivate
      ? await apiService.browse.getPrivatelySharedArticle(props.sharedToken)
      : await apiService.browse.getSharedArticle(props.sharedToken);

    sharedArticle.value = Article.fromJson({
      ...response.data.article,
      ...(!isPrivate ? { shared: { token: props.sharedToken } } : {}),
    }) as Article;

    updateStoreForSharedArticle();

    if (!authService.isGuest()) {
      await loadComments(sharedArticle.value.id);
    }

    const logo = await loadSharedArticleCompanyLogo(sharedArticle.value.id);
    commitSetSharedArticleCompanyLogo(logo);
    store.commit('setCompanyLoading', false);
  } catch (e) {
    handleSharedContentError(e);
  }
};

const tryRedirectToPrivateShare = async (): Promise<boolean> => {
  if (authService.isGuest()) return false;

  try {
    const id = props.articleId || props.workspaceId;
    const isWorkspace = !props.articleId;
    const response = await apiService.sharedArticle.getSharedArticleToken(id, true, isWorkspace);
    const token = response.data?.token;

    if (token) {
      const privateShareUrl = urlService.getPrivatelySharedArticleUrl(token);
      window.history.replaceState({}, '', privateShareUrl);
      await navigationService.navigate({
        name: BROWSE_SHARED,
        params: { sharedToken: token, accessType: 'private' },
      });
      return true;
    }
  } catch {
    // Fallback failed, proceed to throw the original error.
  }
  return false;
};

const selectArticle = async (articleId: string) => {
  const { data } = await apiService.workspace.getArticle(props.workspaceId, articleId);
  const fullArticle = Article.fromJson(data.article);
  commitSetSelectedArticle(fullArticle);

  // Set up autoplay and expand state for the newly loaded article
  commitSetAutoPlay({
    articleId: fullArticle.id,
    workspaceId: props.workspaceId,
    paragraphSlug: props.paragraphSlug || undefined,
  });
  commitSetArticleIdToExpand(fullArticle.id);
  commitSetArticleMode(viewMode.value);
};

const loadAndSelectArticle = async (articleId: string) => {
  try {
    await selectArticle(articleId);
  } catch (error) {
    apiService.handleResponseError(error);
    await router.push({ name: routerNames.WORKSPACES });
  }
};

// Opens the first article that loads. One article failing (SNAP-2449: a row stamped with
// another company 404s) must not redirect the user away from the whole workspace. Errors that
// are not about that one article go to the page-level handler, like any other init failure.
const selectFirstOpenableArticle = async (articleIds: string[]): Promise<boolean> => {
  for (const articleId of articleIds) {
    try {
      await selectArticle(articleId);
      return true;
    } catch (error) {
      if (!isSkippableArticleError(error)) throw error;
      log.error(error);
    }
  }
  return false;
};

const fetchAndSetPageCompany = async (companyId: string) => {
  try {
    const response = await apiService.getCompanyById(companyId);
    if (response.data.company) {
      const company = Company.parse(response.data.company);
      store.commit(Mutations.SET_PAGE_COMPANY, company);
      store.commit('setCompanyLoading', false);
    }
  } catch (error) {
    apiService.handleResponseError(error);
  }
};

const initWorkspace = async () => {
  try {
    const response = await apiService.browse.getWorkspace(props.workspaceId);
    workspace.value = Workspace.parse(response.data.workspace);
  } catch (workspaceError) {
    const redirected = await tryRedirectToPrivateShare();
    if (redirected) return;
    throw workspaceError;
  }

  workspaceService.setState('isSharedMode', false);
  workspaceService.setState('sharedToken', null);
  const isGuest = authService.isGuest();
  const userCompanyId = isGuest ? null : authService.user.company;

  if (!isGuest) {
    workspaceService.setLastWorkspaceToStorage(workspace.value!.id);
  }

  if (isGuest || workspace.value!.company !== userCompanyId) {
    await fetchAndSetPageCompany(workspace.value!.company);
  }

  // Loads the list of summary articles (WorkspaceViewArticleItem)
  await workspaceService.loadWorkspace(workspace.value!);

  if (props.articleId) {
    // If articleId is in the URL, load it directly
    await loadAndSelectArticle(props.articleId);

    const selectedArticle = store.state.workspaceView.selectedArticle;
    // Check for ?diffFrom=N query param to enter diff mode
    const diffFrom = router.currentRoute.query.diffFrom as string | undefined;
    if (diffFrom) {
      const fromVersion = parseInt(diffFrom, 10);
      if (!isNaN(fromVersion) && fromVersion > 0 && workspace.value) {
        commitSetArticleMode(ArticleEditMode.NOTES); // Force notes view for diff
        await dispatchFetchDiff(workspace.value.shortId, selectedArticle.shortId, fromVersion);
        // Remove ?diffFrom from URL (consumed immediately)
        const query = { ...router.currentRoute.query };
        delete query.diffFrom;
        router.replace({ query }).catch(() => {});
      }
    }
  } else {
    // Otherwise, find and load the first article from the workspace order
    const articles = workspaceService.workspaceView.articles;
    const workspaceOrder = workspace.value?.order;

    const articleIds = workspaceOrder?.length ? getOpenableArticleIds(workspaceOrder, articles) : [];

    if (await selectFirstOpenableArticle(articleIds)) {
      // Update the URL to reflect the automatically selected article
      const selectedArticle = store.state.workspaceView.selectedArticle;
      if (selectedArticle) {
        const mode = articleModeService.getArticleMode(selectedArticle);
        const newRoute = urlService.getArticleViewRoute(selectedArticle, workspace.value, mode);
        router.replace(newRoute).catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            log.error(err);
          }
        });
      }
    } else {
      // Empty workspace, or no article could be opened (e.g., all are archived)
      commitSetAutoPlay({ workspaceId: props.workspaceId });
    }
  }
};

const handleInitializationError = (e: unknown) => {
  store.commit('setCompanyLoading', false);
  const error = e as AxiosError<{ error: string; company?: CompanyResponse }>;

  if (error.message === 'Guest access denied') {
    notificationService.error(`You don't have access to this workspace`);
  }

  if (error.response?.data?.company) {
    store.commit(Mutations.SET_COMPANY_STATUS, error.response.data.company.isActivated);
    navigationService.navigate({ name: INACTIVE_COMPANY });
    return;
  }
  if (error.response?.status === 404) {
    log.error(error);
    handleAuthRedirect();
    return;
  }
  if (error.response?.status === 422 && authService.isAuthorized()) {
    notificationService.error(error.response.data.error);
    router.push({ name: HOME });
    return;
  }
  apiService.handleResponseError(error);
};

const initializePage = async () => {
  isLoaded.value = false;
  nextTick(() => {
    loader.value = Loading.service({ lock: true });
  });

  try {
    resetViewState();
    setViewMode();

    if (props.sharedToken) {
      await initSharedContent();
    } else {
      await initWorkspace();
    }

    isLoaded.value = true;
  } catch (error) {
    handleInitializationError(error);
  } finally {
    loader.value.close();
  }
};

watch(
  () => props.workspaceId,
  () => {
    initializePage();
  }
);

watch(
  () => props.articleId,
  (newArticleId, oldArticleId) => {
    // Only react if the page is already loaded and the article ID actually changes.
    // This prevents re-loading on the initial page view.
    if (isLoaded.value && newArticleId && newArticleId !== oldArticleId) {
      commitClearDiffState(); // Clear diff when switching articles
      loadAndSelectArticle(newArticleId);
    }
  }
);

watch(
  () => props.mode,
  () => {
    setViewMode();
    commitSetArticleMode(viewMode.value);
  }
);

onMounted(() => {
  initializePage();
});

onBeforeUnmount(() => {
  store.commit(Mutations.SET_PAGE_COMPANY, null);
  commitWorkspaceViewSetActiveTextTabId(null);
  commitClearDiffState(); // Clear diff when leaving BrowsePage
});

return { __sfc: true,log, props, router, isLoaded, workspace, sharedArticle, loader, viewMode, resetViewState, setViewMode, handleAuthRedirect, handleSharedContentError, loadSharedArticleCompanyLogo, loadComments, updateStoreForSharedArticle, initSharedContent, tryRedirectToPrivateShare, selectArticle, loadAndSelectArticle, selectFirstOpenableArticle, fetchAndSetPageCompany, initWorkspace, handleInitializationError, initializePage, WorkspaceViewPage }
}

})